import { SectionCard } from "@/components/common/SectionCard";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { useRecordContext, useGetList, useGetMany, useDataProvider } from "react-admin";
import { useState, useMemo, useEffect, useCallback, type ReactNode } from "react";
import { StoplossClaimsBulkActions } from "../../resources/stoploss-claims/components/StoplossClaimsDatagrid/StoplossClaimsBulkActions";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Divider,
  Stack,
  Grid2 as Grid,
  Tooltip,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import { useSubmitStoplossClaims } from "@/hooks/useSubmitStoplossClaims";
import { Claimant, ClaimItem, Policy, ResourceName, StoplossClaim } from "@/types";
import { useSelectedPolicyId, useHasPolicyContext } from "@/resources/claimants/components/PolicyContext";
import { TabContent } from "./PageLayout";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import {
  auraTrackChip,
  auraTrackState,
  carrierTrackChip,
  isTwoTrackComplete,
} from "@/resources/stoploss-claims/twoTrackState";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { Link as RouterLink } from "react-router-dom";
import { getProps } from "@/utils/helpers/columnsHelper";

interface ClaimsTabProps {
  context?: "claimant" | "client";
  /** When provided, fetches claims for all listed claimant IDs (family view). */
  familyMemberIds?: string[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// Terminal carrier-lifecycle statuses that are always "done", independent of the
// two tracks. The "submitted" boundary is no longer a blanket "done" — it's now
// policy-conditional (#5392), derived per claim via isTwoTrackComplete: an Accel
// policy needs BOTH funded AND carrier-submitted; a non-Aura policy needs carrier
// alone. A reimbursed/denied/closed claim is terminal regardless of the tracks.
const TERMINAL_STATUSES = ["reimbursed", "denied", "closed"];

// Claim enriched in the component with the claimant name (client/family view)
// and its policy's Aura-funding flag (resolved via policy_id_ref).
type TwoTrackClaim = StoplossClaim & { claimant_name?: string; policyAccelEnabled?: boolean };

/**
 * The claimant a claim is SHOWN under (#7244): its live survivor if a merged,
 * retired record still owns it (a safety net -- the merge moves every claim),
 * else its owner. Row actions keep acting on claimant_id_ref; only grouping,
 * names and family filtering use this.
 */
const displayClaimantId = (c: StoplossClaim): string => c.merged_into_claimant_id_ref ?? c.claimant_id_ref;

// Per-row track action (#5380). Shown in a track cell when that track hasn't been
// actioned; calls the shared submit hook for a single claim, with an in-flight
// disabled/spinner state keyed to row+track so it can't be double-submitted.
type TrackTarget = "aura" | "carrier";
// In-flight per-row actions, keyed by `${id}:${track}` so concurrent actions on
// different rows/tracks each track their own spinner/disabled state independently
// (#5380 double-submit guard — a single slot would let one action clear another's).
type PendingTrackAction = ReadonlySet<string>;
const trackActionKey = (id: string, track: TrackTarget): string => `${id}:${track}`;
type TrackActionHandler = (claim: StoplossClaim, track: TrackTarget) => void;

const TrackActionButton = ({
  label,
  color,
  icon,
  claim,
  track,
  onAction,
  pending,
}: {
  label: string;
  color: "primary" | "secondary";
  icon: ReactNode;
  claim: StoplossClaim;
  track: TrackTarget;
  onAction: TrackActionHandler;
  pending: PendingTrackAction;
}) => {
  const isPending = pending.has(trackActionKey(String(claim.id), track));
  return (
    <Button
      size="small"
      variant="text"
      color={color}
      disabled={isPending}
      aria-label={`${label} — claim ${(claim as TwoTrackClaim).aegle_id || claim.id}`}
      startIcon={isPending ? <CircularProgress size={12} color="inherit" /> : icon}
      onClick={(e) => {
        e.stopPropagation();
        onAction(claim, track);
      }}
      sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 0.75, minWidth: 0 }}
    >
      {label}
    </Button>
  );
};

// Two-track summary header (#5394) — one card per track, side by side, mirroring
// the Aura/Carrier columns in the table below. Deliberately NOT the shared
// MetricCard grid (which reads identically to the client dashboard); the dot ties
// each card to its column accent (Aura = primary/teal, carrier = secondary).
const TrackSummaryCard = ({
  label,
  dotColor,
  stats,
}: {
  label: string;
  dotColor: string;
  stats: { value: number; label: string }[];
}) => (
  <Box
    sx={{
      bgcolor: "background.paper",
      border: "0.5px solid",
      borderColor: "divider",
      borderRadius: "12px",
      px: 2.5,
      py: 2,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: dotColor, flexShrink: 0 }} />
      <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", gap: 4 }}>
      {stats.map((s) => (
        <Box key={s.label}>
          <Typography variant="h5" sx={{ fontWeight: 500, lineHeight: 1 }}>
            {s.value}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {s.label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

// ─── Shared column building blocks ──────────────────────────────────────────

const typeColumn: CompactTableColumn<StoplossClaim> = {
  key: "type",
  label: getProps("stoploss_claim_type", ResourceName.STOPLOSS_CLAIMS).label,
  width: "80px",
  render: (claim) => (
    <>
      <Chip
        label={formatToTitleCase(claim.stoploss_claim_type || "—")}
        size="small"
        variant="outlined"
        sx={{ height: 22, fontSize: "0.7rem" }}
      />
      {/* #7244: a frozen claim still held by a record merged into this one. */}
      {claim.merged_into_claimant_id_ref && (
        <Typography variant="caption" color="text.secondary" display="block" data-testid="claim-from-merged-record">
          From merged record
        </Typography>
      )}
    </>
  ),
};

const totalPaidColumn: CompactTableColumn<StoplossClaim> = {
  key: "total_paid",
  label: getProps("total_paid_amount", ResourceName.STOPLOSS_CLAIMS).label,
  width: "1fr",
  align: "right",
  render: (claim) => (
    <Typography variant="body2" fontWeight={500}>
      {claim.total_paid_amount ? formatUSD(claim.total_paid_amount) : "—"}
    </Typography>
  ),
};

// ─── Two-track status columns (#5379 chips + #5380 per-row actions) ──────────
// Each cell shows the completed status chip once the track is actioned, else the
// per-row action button. Aura (teal/primary) is policy-gated — N/A for non-Accel
// policies. Carrier = secondary. Built per-render (factories) so the action can
// close over the submit handler + in-flight state.

const makeAuraColumn = (
  onAction: TrackActionHandler,
  pending: PendingTrackAction
): CompactTableColumn<StoplossClaim> => ({
  key: "aura_funding",
  label: "Aura funding",
  width: "150px",
  render: (claim) => {
    if (!(claim as TwoTrackClaim).policyAccelEnabled) {
      return (
        <Typography variant="caption" color="text.secondary">
          N/A
        </Typography>
      );
    }
    // Rejected (#5382 / #5332): inline error state that stays retryable. The
    // reason text is surfaced by the Aura sender response (toast) at submit time;
    // once a persisted rejection-reason field exists it can fill the tooltip.
    if (auraTrackState(claim) === "rejected") {
      return (
        <Stack spacing={0.25} alignItems="flex-start">
          <Tooltip title="Aura funding was rejected — re-request to retry">
            <span>
              <StatusChip label="Rejected" color="error" size={UIComponentSize.Small} />
            </span>
          </Tooltip>
          <TrackActionButton
            label="Retry"
            color="primary"
            icon={<CloudUploadIcon fontSize="small" />}
            claim={claim}
            track="aura"
            onAction={onAction}
            pending={pending}
          />
        </Stack>
      );
    }
    const chip = auraTrackChip(claim);
    return chip ? (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <StatusChip label={chip.label} color={chip.color} size={UIComponentSize.Small} />
      </Box>
    ) : (
      <TrackActionButton
        label="Request Funding"
        color="primary"
        icon={<CloudUploadIcon fontSize="small" />}
        claim={claim}
        track="aura"
        onAction={onAction}
        pending={pending}
      />
    );
  },
});

const makeCarrierColumn = (
  onAction: TrackActionHandler,
  pending: PendingTrackAction
): CompactTableColumn<StoplossClaim> => ({
  key: "carrier",
  label: "Carrier",
  width: "160px",
  render: (claim) => {
    const chip = carrierTrackChip(claim);
    return chip ? (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <StatusChip label={chip.label} color={chip.color} size={UIComponentSize.Small} />
      </Box>
    ) : (
      <TrackActionButton
        label="Mark as Submitted"
        color="secondary"
        icon={<SendIcon fontSize="small" />}
        claim={claim}
        track="carrier"
        onAction={onAction}
        pending={pending}
      />
    );
  },
});

const claimantColumn: CompactTableColumn<StoplossClaim> = {
  key: "claimant",
  label: "Claimant",
  width: "1fr",
  render: (claim) => {
    const name = (claim as TwoTrackClaim).claimant_name || "—";
    return (
      <Tooltip title={name} enterDelay={500}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {name}
        </Typography>
      </Tooltip>
    );
  },
};

// Column sets are composed inside the component (the two-track columns are
// factories that close over the submit handler + in-flight state). Widen
// total_paid in the client/family view, where the claimant column takes the 1fr.
const widenTotalPaid = (col: CompactTableColumn<StoplossClaim>): CompactTableColumn<StoplossClaim> =>
  col.key === "total_paid" ? { ...col, width: "120px" } : col;

// ─── Claim items expand panel ───────────────────────────────────────────────

const MAX_VISIBLE_ITEMS = 15;

const ClaimItemsExpandRow = ({
  claim,
  showClaimantName = false,
}: {
  claim: StoplossClaim;
  showClaimantName?: boolean;
}) => {
  const dataProvider = useDataProvider();
  const [items, setItems] = useState<ClaimItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    dataProvider
      .getList(ResourceName.CLAIM_ITEMS, {
        filter: { stoploss_claim_id_ref: claim.id },
        pagination: { page: 1, perPage: MAX_VISIBLE_ITEMS + 1 },
        sort: { field: "paid_date", order: "DESC" },
      })
      .then(({ data }: { data: ClaimItem[] }) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [claim.id, dataProvider]);

  // Fetch claimant names for expanded items when in family view
  const itemClaimantIds = useMemo(
    () => (showClaimantName ? [...new Set(items.map((i) => i.claimant_id_ref).filter(Boolean))] : []),
    [items, showClaimantName]
  );
  const { data: itemClaimants = [] } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    { ids: itemClaimantIds },
    { enabled: itemClaimantIds.length > 0 }
  );
  const itemClaimantMap = useMemo(() => new Map(itemClaimants.map((c) => [c.id, c.claimant_name])), [itemClaimants]);

  if (loading) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ py: 1 }}>
        Loading claim items…
      </Typography>
    );
  }

  if (items.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ py: 1 }}>
        No claim items
      </Typography>
    );
  }

  const hasMore = items.length > MAX_VISIBLE_ITEMS;
  const visibleItems = hasMore ? items.slice(0, MAX_VISIBLE_ITEMS) : items;

  return (
    <Box>
      <Stack divider={<Divider sx={{ borderStyle: "dashed" }} />}>
        {visibleItems.map((item) => (
          <Box
            key={item.id}
            component={RouterLink}
            to={`/claim-items/${item.id}/show`}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 0.75,
              textDecoration: "none",
              color: "inherit",
              "&:hover": { opacity: 0.7 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              {showClaimantName && itemClaimantMap.size > 0 && (
                <>
                  <Typography variant="caption" fontWeight={500} noWrap>
                    {itemClaimantMap.get(item.claimant_id_ref) || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    ·
                  </Typography>
                </>
              )}
              <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                {item.provider || "Unknown provider"}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                ·
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatLongDate(item.date_of_service)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="caption" fontWeight={500}>
                {item.paid_amount ? formatUSD(item.paid_amount) : "—"}
              </Typography>
              <StatusChip
                label={formatToTitleCase(item.claim_status || "—")}
                color={getStatusColor(item.claim_status) ?? ColorType.Default}
                size={UIComponentSize.Small}
              />
            </Box>
          </Box>
        ))}
      </Stack>
      {hasMore && (
        <Box sx={{ pt: 0.75 }}>
          <Typography
            component={RouterLink}
            to={`/stoploss-claims/${claim.id}/show`}
            variant="caption"
            color="primary"
            sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            View all claim items →
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Ineligible claim item columns ──────────────────────────────────────────

const ineligibleColumns: CompactTableColumn<ClaimItem>[] = [
  {
    key: "provider",
    label: getProps("provider", ResourceName.CLAIM_ITEMS).label,
    width: "1fr",
    render: (item) => (
      <Typography variant="body2" noWrap>
        {item.provider || "—"}
      </Typography>
    ),
  },
  {
    key: "type",
    label: getProps("claim_type", ResourceName.CLAIM_ITEMS).label,
    width: "80px",
    render: (item) => (
      <Typography variant="caption" color="text.secondary">
        {formatToTitleCase(item.claim_type || "—")}
      </Typography>
    ),
  },
  {
    key: "paid_amount",
    label: getProps("paid_amount", ResourceName.CLAIM_ITEMS).label,
    width: "100px",
    align: "right",
    render: (item) => (
      <Typography variant="body2" fontWeight={500}>
        {item.paid_amount ? formatUSD(item.paid_amount) : "—"}
      </Typography>
    ),
  },
  {
    key: "status",
    label: getProps("claim_status", ResourceName.CLAIM_ITEMS).label,
    width: "100px",
    align: "right",
    render: (item) => (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <StatusChip
          label={formatToTitleCase(item.claim_status || "—")}
          color={getStatusColor(item.claim_status) ?? ColorType.Default}
          size={UIComponentSize.Small}
        />
      </Box>
    ),
  },
];

/** Ineligible columns with claimant name prepended (family/client view) */
const familyIneligibleColumns: CompactTableColumn<ClaimItem>[] = [
  {
    key: "claimant_name",
    label: "Claimant",
    width: "1fr",
    render: (item) => (
      <Typography variant="body2" noWrap>
        {(item as ClaimItem & { claimant_name?: string }).claimant_name || "—"}
      </Typography>
    ),
  },
  ...ineligibleColumns,
];

// ─── Panel header ───────────────────────────────────────────────────────────

const PanelHeader = ({ title, count, action }: { title: string; count: number; action?: React.ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 1,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
      <Chip label={count} size="small" sx={{ height: 20, fontSize: "0.7rem" }} />
    </Box>
    {action}
  </Box>
);

// ─── Main component ─────────────────────────────────────────────────────────

export const ClaimsTab = ({ context = "claimant", familyMemberIds }: ClaimsTabProps) => {
  const isFamilyView = context === "claimant" && familyMemberIds && familyMemberIds.length > 1;
  const record = useRecordContext();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [ineligibleExpanded, setIneligibleExpanded] = useState(false);

  // Per-row track actions (#5380). The submit hook toasts + refreshes; we track
  // each in-flight row+track by key so its button shows a spinner and can't
  // double-submit — concurrent actions on other rows don't clear this one.
  const { submit } = useSubmitStoplossClaims();
  const [pendingAction, setPendingAction] = useState<PendingTrackAction>(() => new Set());
  const handleTrackAction = useCallback<TrackActionHandler>(
    (claim, track) => {
      const key = trackActionKey(String(claim.id), track);
      setPendingAction((prev) => new Set(prev).add(key));
      void submit([claim], track)
        .then((ok) => {
          // Drop the row from any bulk selection on a successful per-row action so a
          // later bulk run can't re-submit an already-actioned claim (#5378 review).
          if (ok) setSelectedIds((ids) => ids.filter((id) => id !== String(claim.id)));
        })
        .finally(() =>
          // Guarded delete: only clears this action's key, never another in-flight row's.
          setPendingAction((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          })
        );
    },
    [submit]
  );

  const contextPolicyId = useSelectedPolicyId();
  const [localSelectedPolicyId, setLocalSelectedPolicyId] = useState<string>("all");

  const { data: fetchedPolicies = [] } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: record?.id },
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: context === "client" }
  );

  const policies = context === "claimant" ? record?.policies || [] : fetchedPolicies;
  const hasPolicyContext = useHasPolicyContext();

  // Use global PolicyContext if a PolicyProvider wraps this component, else fall back to local state
  const selectedPolicyId =
    context === "claimant"
      ? contextPolicyId
      : hasPolicyContext
        ? contextPolicyId // From global PolicyProvider (client show)
        : localSelectedPolicyId === "all"
          ? undefined
          : localSelectedPolicyId;

  // Clear any bulk selection when the policy filter changes (#5378 review):
  // otherwise the "N selected" count includes now-hidden rows and a later bulk
  // action could target claims not visible under the new filter.
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedPolicyId]);

  // Family view fetches by client (broader) then filters in-memory to family members
  const familyMemberIdSet = useMemo(
    () => (isFamilyView ? new Set(familyMemberIds) : null),
    [isFamilyView, familyMemberIds]
  );

  const stoplossFilter = useMemo(() => {
    const base =
      context === "claimant"
        ? isFamilyView
          ? { client_id_ref: record?.client_id_ref }
          : { claimant_id_ref: record?.id }
        : { client_id_ref: record?.id };
    return selectedPolicyId ? { ...base, policy_id_ref: selectedPolicyId } : base;
  }, [context, record?.id, record?.client_id_ref, selectedPolicyId, isFamilyView]);

  const claimItemsFilter = useMemo(() => {
    const base =
      context === "claimant"
        ? isFamilyView
          ? { client_id_ref: record?.client_id_ref, is_eligible: false }
          : { claimant_id_ref: record?.id, is_eligible: false }
        : { client_id_ref: record?.id, is_eligible: false };
    return selectedPolicyId ? { ...base, policy_id_ref: selectedPolicyId } : base;
  }, [context, record?.id, record?.client_id_ref, selectedPolicyId, isFamilyView]);

  const { data: rawStoplossClaims = [] } = useGetList<StoplossClaim>(ResourceName.STOPLOSS_CLAIMS, {
    filter: stoplossFilter,
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "id", order: "ASC" },
  });

  const { data: rawIneligibleClaimItems = [] } = useGetList<ClaimItem>(ResourceName.CLAIM_ITEMS, {
    filter: claimItemsFilter,
    pagination: { page: 1, perPage: 10000 },
  });

  // In family view, filter the broader client results to only family members
  const stoplossClaims = useMemo(
    () =>
      familyMemberIdSet
        ? rawStoplossClaims.filter((c) => familyMemberIdSet.has(displayClaimantId(c)))
        : rawStoplossClaims,
    [rawStoplossClaims, familyMemberIdSet]
  );

  const ineligibleClaimItems = useMemo(
    () =>
      familyMemberIdSet
        ? rawIneligibleClaimItems.filter((c) => familyMemberIdSet.has(c.claimant_id_ref))
        : rawIneligibleClaimItems,
    [rawIneligibleClaimItems, familyMemberIdSet]
  );

  // Enrich claims with claimant names for client or family context
  const claimantIds = useMemo(() => {
    if (context !== "client" && !isFamilyView) return [];
    const ids = new Set<string>();
    for (const c of stoplossClaims) if (displayClaimantId(c)) ids.add(displayClaimantId(c));
    for (const ci of ineligibleClaimItems) if (ci.claimant_id_ref) ids.add(ci.claimant_id_ref);
    return [...ids];
  }, [stoplossClaims, ineligibleClaimItems, context, isFamilyView]);

  const { data: claimants = [] } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    {
      ids: claimantIds,
    },
    { enabled: claimantIds.length > 0 }
  );

  const claimantMap = useMemo(() => new Map(claimants.map((c) => [c.id, c.claimant_name])), [claimants]);

  // Per-claim Aura-funding gate (#5379/#5380): resolve each claim's policy flag
  // via policy_id_ref. is_accel_funding_enabled comes from the policy read model
  // (US 5574), so the Aura column/action only shows for Accel/Prompt-Pay policies.
  const policyAccelMap = useMemo<Map<string, boolean>>(
    () =>
      new Map(
        (policies as Policy[]).map((p): [string, boolean] => [String(p.id), Boolean(p.is_accel_funding_enabled)])
      ),
    [policies]
  );

  const enrichedClaims = useMemo<TwoTrackClaim[]>(
    () =>
      stoplossClaims.map((c) => ({
        ...c,
        ...(context === "client" || isFamilyView ? { claimant_name: claimantMap.get(displayClaimantId(c)) || "" } : {}),
        policyAccelEnabled: c.policy_id_ref ? (policyAccelMap.get(String(c.policy_id_ref)) ?? false) : false,
      })),
    [stoplossClaims, claimantMap, policyAccelMap, context, isFamilyView]
  );

  // Split claims into actionable vs completed using the policy-conditional
  // two-track "done" derivation (#5392): terminal carrier states are always done;
  // otherwise an Accel policy needs both tracks (funded + carrier-submitted) while
  // a non-Aura policy needs carrier submission alone. So an Accel claim that's
  // carrier-submitted but not yet funded stays in "Ready for Submission".
  const isClaimComplete = useCallback(
    (c: TwoTrackClaim) =>
      TERMINAL_STATUSES.includes(c.claim_status) || isTwoTrackComplete(c, Boolean(c.policyAccelEnabled)),
    []
  );
  const actionableClaims = useMemo(
    () => enrichedClaims.filter((c) => !isClaimComplete(c)),
    [enrichedClaims, isClaimComplete]
  );
  const completedClaims = useMemo(
    () => enrichedClaims.filter((c) => isClaimComplete(c)),
    [enrichedClaims, isClaimComplete]
  );

  // Two-track summary counts (#5394). "Awaiting" = still to do (actionable, not
  // yet done on that track); "done" totals span all claims on the tab. Aura is
  // gated to Accel policies.
  const awaitingAura = useMemo(
    () => actionableClaims.filter((c) => c.policyAccelEnabled && auraTrackState(c) !== "funded").length,
    [actionableClaims]
  );
  const auraFunded = useMemo(
    () => enrichedClaims.filter((c) => c.policyAccelEnabled && auraTrackState(c) === "funded").length,
    [enrichedClaims]
  );
  const awaitingCarrier = useMemo(
    () => actionableClaims.filter((c) => !c.carrier_submitted_at).length,
    [actionableClaims]
  );
  const carrierSubmitted = useMemo(
    () => enrichedClaims.filter((c) => Boolean(c.carrier_submitted_at)).length,
    [enrichedClaims]
  );

  const showClaimantName = context === "client" || isFamilyView;

  // Hide the whole Aura funding track (column + summary card) when it can't
  // apply — otherwise it's just a column of "N/A". A specific policy shows Aura
  // only when that policy is Accel-enabled; on "All policies" we keep it whenever
  // ANY loaded policy is Accel, since the list can mix Accel/non-Accel claims.
  const showAuraTrack = useMemo(() => {
    if (selectedPolicyId) return policyAccelMap.get(String(selectedPolicyId)) === true;
    return Array.from(policyAccelMap.values()).some(Boolean);
  }, [selectedPolicyId, policyAccelMap]);

  // Compose the two-track columns here so the per-row actions close over the
  // submit handler + in-flight state. Both panels use the same two-track view;
  // client/family prepends the claimant column and drops type for width. The
  // Aura column is dropped entirely when the track doesn't apply (see above).
  const trackCols = useMemo(
    () => [
      ...(showAuraTrack ? [makeAuraColumn(handleTrackAction, pendingAction)] : []),
      makeCarrierColumn(handleTrackAction, pendingAction),
    ],
    [showAuraTrack, handleTrackAction, pendingAction]
  );
  const columns = useMemo<CompactTableColumn<StoplossClaim>[]>(
    () =>
      showClaimantName
        ? [claimantColumn, widenTotalPaid(totalPaidColumn), ...trackCols]
        : [typeColumn, totalPaidColumn, ...trackCols],
    [showClaimantName, trackCols]
  );
  const actCols = columns;
  const compCols = columns;

  return (
    <TabContent>
      {/* Family view indicator */}
      {isFamilyView && (
        <Alert
          severity="info"
          icon={<FamilyRestroomIcon fontSize="small" />}
          sx={{ mb: 2, "& .MuiAlert-message": { fontSize: "0.8125rem" } }}
        >
          Showing claims for the entire family unit, not just this claimant.
        </Alert>
      )}

      {/* Two-track summary (#5394) — one card per track, mirroring the columns
          below. Distinct from the dashboard's shared MetricCard grid. */}
      <Box
        sx={{
          mb: 2,
          display: "grid",
          // Always reserve two columns so a card is the same size whether or not
          // the Aura track applies — a lone carrier card sits in the left slot
          // rather than stretching full-width.
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        {showAuraTrack && (
          <TrackSummaryCard
            label="Aura funding"
            dotColor="primary.main"
            stats={[
              { value: awaitingAura, label: "Awaiting funding" },
              { value: auraFunded, label: "Funded" },
            ]}
          />
        )}
        <TrackSummaryCard
          label="Carrier"
          dotColor="secondary.main"
          stats={[
            { value: awaitingCarrier, label: "Awaiting submission" },
            { value: carrierSubmitted, label: "Submitted" },
          ]}
        />
      </Box>

      {/* Policy selector for client context — hide if global PolicyProvider exists */}
      {context === "client" && !hasPolicyContext && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Policy</InputLabel>
            <Select
              value={localSelectedPolicyId}
              label="Filter by Policy"
              onChange={(e) => setLocalSelectedPolicyId(e.target.value)}
            >
              <MenuItem value="all">All Policies</MenuItem>
              {policies.map((policy: Policy) => (
                <MenuItem key={policy.id} value={policy.id}>
                  {policy.aegle_id || policy.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Two-column layout: Actionable (left) | Completed (right) */}
      <Grid container spacing={2}>
        {/* Left — claims ready for action + ineligible items */}
        <Grid size={{ xs: 12, xl: 7 }}>
          <PanelHeader
            title="Ready for Submission"
            count={actionableClaims.length}
            action={
              selectedIds.length > 0 ? (
                <StoplossClaimsBulkActions
                  selectedIds={selectedIds}
                  onUnselectAll={() => setSelectedIds([])}
                  allClaims={actionableClaims}
                />
              ) : undefined
            }
          />
          <CompactTable
            data={actionableClaims}
            columns={actCols}
            rowLink={(claim) => `/stoploss-claims/${claim.id}/show`}
            rowsPerPage={10}
            emptyMessage="No claims ready for submission"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            expandRow={(claim) => <ClaimItemsExpandRow claim={claim} showClaimantName={showClaimantName} />}
            expandLabel={(claim) => (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                {claim.claim_items_count ?? 0}
              </Typography>
            )}
          />

          {/* Ineligible Claim Items — below ready for submission */}
          {ineligibleClaimItems.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <SectionCard
                title="Ineligible Claim Items"
                compact
                noPadding
                collapsible
                isExpanded={ineligibleExpanded}
                onToggle={() => setIneligibleExpanded(!ineligibleExpanded)}
                badge={
                  <Chip label={ineligibleClaimItems.length} size="small" sx={{ height: 20, fontSize: "0.7rem" }} />
                }
              >
                <CompactTable
                  data={
                    showClaimantName
                      ? ineligibleClaimItems.map((ci) => ({
                          ...ci,
                          claimant_name: claimantMap.get(ci.claimant_id_ref) || "",
                        }))
                      : ineligibleClaimItems
                  }
                  columns={showClaimantName ? familyIneligibleColumns : ineligibleColumns}
                  rowLink={(item) => `/claim-items/${item.id}/show`}
                  rowsPerPage={10}
                  emptyMessage="No ineligible claim items"
                />
              </SectionCard>
            </Box>
          )}
        </Grid>

        {/* Right — submitted/completed claims */}
        <Grid size={{ xs: 12, xl: 5 }}>
          <PanelHeader title="Submitted / Completed" count={completedClaims.length} />
          <CompactTable
            data={completedClaims}
            columns={compCols}
            rowLink={(claim) => `/stoploss-claims/${claim.id}/show`}
            rowsPerPage={10}
            emptyMessage="No submitted claims yet"
            expandRow={(claim) => <ClaimItemsExpandRow claim={claim} showClaimantName={showClaimantName} />}
            expandLabel={(claim) => (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                {claim.claim_items_count ?? 0}
              </Typography>
            )}
          />
        </Grid>
      </Grid>
    </TabContent>
  );
};

export const ClaimantClaimsTab = ({ familyMemberIds }: { familyMemberIds?: string[] }) => (
  <ClaimsTab context="claimant" familyMemberIds={familyMemberIds} />
);
export const ClientClaimsTab = () => <ClaimsTab context="client" />;
